import { useState } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { toast } from '@campai/lib-web/components'
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Checkbox,
} from '@campai/lib-web/components/new/ui'
import { client } from '@campai/lib-web/utils/trpc'
import { notificationCategoriesByTarget } from '@campai/lib-common/entities'
import { hasAnyAdminOrganizationAccess } from '@campai/lib-common/utils/organization'
import type { NotificationCategory, ClientAppType, UserNotificationSettings } from '@campai/lib-common/entities'

type NotificationChannel = 'email' | 'push'
type NotificationChannelSettings = { email: boolean; push: boolean }

import { appState } from '@/appState'

const targetOrder = ['connect', 'root', 'admin'] as const satisfies readonly ClientAppType[]

const cloneNotificationSettings = (settings: UserNotificationSettings): UserNotificationSettings => ({
    ...settings,
    categories: Object.fromEntries(
        (Object.entries(settings.categories) as Array<[NotificationCategory, NotificationChannelSettings]>).map(
            ([category, channels]) => [
                category,
                {
                    email: channels.email,
                    push: channels.push,
                },
            ]
        )
    ) as UserNotificationSettings['categories'],
})

export const Notifications = observer(() => {
    const { t } = useTranslation()
    const [settings, setSettings] = useState(() => cloneNotificationSettings(appState.user.notifications))
    const [isSaving, setIsSaving] = useState(false)

    const availableTargets = targetOrder.filter((target) => {
        if (target === 'connect') {
            return true
        }

        return hasAnyAdminOrganizationAccess(appState.user)
    })

    const visibleCategories = availableTargets.flatMap((target) => notificationCategoriesByTarget[target])
    const hasVisibleCategories = visibleCategories.length > 0
    const i18nKey = 'profile.notifications'

    const getChannelValue = (category: NotificationCategory, channel: NotificationChannel) =>
        settings.categories[category]?.[channel] !== false

    const handleToggle = (category: NotificationCategory, channel: NotificationChannel, checked: boolean) => {
        setSettings((currentSettings) => ({
            ...currentSettings,
            categories: {
                ...currentSettings.categories,
                [category]: {
                    email: currentSettings.categories[category]?.email ?? true,
                    push: currentSettings.categories[category]?.push ?? true,
                    [channel]: checked,
                },
            },
        }))
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            await client.currentUser.updateNotificationSettings.mutate(settings)
            runInAction(() => {
                appState.user = {
                    ...appState.user,
                    notifications: settings,
                }
            })
            toast.success(t(`${i18nKey}.saved`))
        } catch (error) {
            toast.error(error instanceof Error ? error.message : t(`${i18nKey}.error`))
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Card>
            <CardHeader className="border-b">
                <CardTitle>{t(`${i18nKey}.title`)}</CardTitle>
                <CardDescription>{t(`${i18nKey}.description`)}</CardDescription>
            </CardHeader>
            <CardContent>
                {hasVisibleCategories ? (
                    <div className="space-y-6">
                        {availableTargets.map((target) => {
                            const categories = notificationCategoriesByTarget[target]

                            if (!categories.length) {
                                return null
                            }

                            return (
                                <div key={target} className="space-y-3">
                                    <div className="text-sm font-medium">{t(`${i18nKey}.targets.${target}`)}</div>
                                    <div className="overflow-hidden rounded-md border">
                                        <div className="bg-muted/50 grid grid-cols-[minmax(0,1fr)_56px_56px] border-b px-3 py-2 text-sm font-medium sm:grid-cols-[minmax(0,1fr)_96px_96px]">
                                            <div>{t(`${i18nKey}.category`)}</div>
                                            <div className="text-center">{t(`${i18nKey}.channels.email`)}</div>
                                            <div className="text-center">{t(`${i18nKey}.channels.push`)}</div>
                                        </div>
                                        {categories.map((category) => (
                                            <div
                                                key={category}
                                                className="grid grid-cols-[minmax(0,1fr)_56px_56px] items-center border-b px-3 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_96px_96px]"
                                            >
                                                <div className="min-w-0 text-sm">
                                                    {t(`entities.notification.category.${category}`)}
                                                </div>
                                                <div className="flex justify-center">
                                                    <Checkbox
                                                        checked={getChannelValue(category, 'email')}
                                                        aria-label={t(`${i18nKey}.aria.email`, {
                                                            category: t(`entities.notification.category.${category}`),
                                                        })}
                                                        onCheckedChange={(checked) =>
                                                            handleToggle(category, 'email', checked === true)
                                                        }
                                                    />
                                                </div>
                                                <div className="flex justify-center">
                                                    <Checkbox
                                                        checked={getChannelValue(category, 'push')}
                                                        aria-label={t(`${i18nKey}.aria.push`, {
                                                            category: t(`entities.notification.category.${category}`),
                                                        })}
                                                        onCheckedChange={(checked) =>
                                                            handleToggle(category, 'push', checked === true)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-muted-foreground text-sm">{t(`${i18nKey}.empty`)}</div>
                )}
            </CardContent>
            <CardFooter className="justify-end">
                <Button onClick={() => void handleSave()} loading={isSaving}>
                    {t('actions.saveChanges')}
                </Button>
            </CardFooter>
        </Card>
    )
})
